import { Observable } from 'rxjs';

/**
 * Options read by the typeahead controls' option lists (moved out of dynamic.interface.ts,
 * over the 500-line gate, when US 35432 added `itemHint`; same split as WI 34968).
 */
export interface IDynamicTypeaheadOptions {
  // typeahead: show id as a prefix on each drop down option label
  displayIdPrefix?: boolean;
  // typeahead: explicit data service resolver
  dataResolver?: () => Promise<unknown>;
  // typeahead: manual collection of options
  dropdownOptions?: Array<unknown>;
  // typeahead: dropdown popup list height in px (default Kendo 200); taller shows more options before scrolling
  listHeight?: number;
  // web typeahead (`ups-dynamic-typeahead`): popup width in px; unset = the control's width (US 35728)
  popupWidth?: number;
  /**
   * Web customer typeahead (`ups-dynamic-typeahead-customers`, single select): opt-in clear (X) button
   * shown while a customer is picked and the control is enabled (Bug 35680). Clicking it nulls the form
   * control and emits `valueChange` with null, like any other selection. Off by default, so the other
   * apps that share the dropdown (web-safety, the estimate builder) are unchanged.
   */
  clearable?: boolean;
  /**
   * Web customer typeahead (`ups-dynamic-typeahead-customers`): an opt-in second source searched with the same term, its rows
   * listed after the customer service's (US 36095: the SRF lists dbo prospects in dbo mode). An error there lists the customers
   * alone. Set in code only. Off by default, so the other apps that share the dropdown are unchanged.
   */
  // eslint-disable-next-line no-unused-vars -- pre-commit-allow: base rule false positive on a TS function-type parameter name
  additionalCustomerSearch?: (searchString: string) => Observable<unknown[]>;
  /**
   * Web typeahead (`ups-dynamic-typeahead`): an optional hint for one option, shown as its hover
   * title only (US 35432 Opportunity Stage tips; US 35728, Tim: "the description should only be on
   * tooltip not dropdown"). Return null for no hint. Set in code only: a function does not survive
   * a JSON-stored form definition.
   */
  // eslint-disable-next-line no-unused-vars -- pre-commit-allow: base rule false positive on a TS function-type parameter name (US 35748 touch)
  itemHint?: (item: unknown) => string | null | undefined;
  /**
   * Option ids a typeahead never offers (compared case-insensitively against the mapped item id).
   * Applied to every option list the control builds, so a value already held by the form control
   * still displays through the control's own value seed. Opt-in; unset leaves the list untouched
   * (US 35748: Opportunity Stage hides Expired).
   */
  excludeValueIds?: readonly string[];
  /**
   * NativeScript generic typeahead modal (`ups-listview-row-typeahead`): optional extra
   * utility classes on the secondary line (e.g. `text-xs` when descriptions are long).
   */
  typeaheadSecondaryTextClass?: string;
  /**
   * Web facility typeahead (`ups-dynamic-typeahead-facility`): also list Sparta PROSPECT sites
   * after the Viewpoint facilities, each row labelled Customer or Prospect (US 36008). Skipped
   * while the scope control (scopeQueryWhenFormControlIsSet) holds a value, and a failed prospect
   * lookup still shows the Viewpoint rows. Opt-in; unset leaves the picker exactly as before.
   */
  includeProspectFacilities?: boolean;
}
